
import React, { useMemo } from 'react';
import { NewsItem, Product } from '../types';
import { FileQuestion, Home, ArrowRight, Search, AlertTriangle, Package, Newspaper } from 'lucide-react';

interface NotFoundPageProps {
  brokenSlug: string;
  news: NewsItem[];
  products: Product[];
  onNavigateHome: () => void;
  onNavigateToArticle: (article: NewsItem) => void;
}

const NotFoundPage: React.FC<NotFoundPageProps> = ({ 
  brokenSlug, 
  news, 
  products, 
  onNavigateHome, 
  onNavigateToArticle 
}) => {
  
  // Lógica de Recomendação Inteligente
  const suggestions = useMemo(() => {
    // 1. Limpar o slug para palavras-chave (remove traços e palavras comuns)
    const keywords = brokenSlug
      .replace(/-/g, ' ')
      .toLowerCase()
      .split(' ')
      .filter(w => w.length > 3 && !['para', 'com', 'dos', 'das', 'uma', 'que'].includes(w));

    // 2. Buscar em Notícias
    const matchedNews = news.filter(n => {
      const text = (n.title + ' ' + n.category).toLowerCase();
      return keywords.some(k => text.includes(k));
    }).slice(0, 2);

    // 3. Buscar em Produtos
    const matchedProducts = products.filter(p => {
      const text = (p.title + ' ' + p.description).toLowerCase();
      return keywords.some(k => text.includes(k));
    }).slice(0, 2);

    // 4. Se não tiver matches suficientes, preencher com os mais recentes/destaques
    let results: Array<{ type: 'news' | 'product', data: any }> = [
        ...matchedNews.map(n => ({ type: 'news' as const, data: n })),
        ...matchedProducts.map(p => ({ type: 'product' as const, data: p }))
    ];

    if (results.length < 3) {
       const remainingSlots = 4 - results.length;
       const latestNews = news
        .filter(n => !results.some(r => r.type === 'news' && r.data.id === n.id))
        .slice(0, remainingSlots)
        .map(n => ({ type: 'news' as const, data: n }));
       
       results = [...results, ...latestNews];
    }

    return results;
  }, [brokenSlug, news, products]);

  return (
    <div className="min-h-screen bg-[#f8fafc] pt-32 pb-20 px-4 animate-fade-in-up flex flex-col items-center">
      
      {/* 404 Hero Section */}
      <div className="max-w-2xl w-full text-center space-y-6 mb-16">
        <div className="relative inline-block">
            <div className="absolute inset-0 bg-red-100 rounded-full blur-2xl opacity-50 animate-pulse"></div>
            <div className="relative bg-white p-6 rounded-3xl shadow-xl border border-red-50 inline-flex">
                <FileQuestion size={64} className="text-red-500" />
            </div>
        </div>
        
        <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
            Página não encontrada
        </h1>
        
        <div className="bg-white border border-gray-200 p-4 rounded-2xl max-w-lg mx-auto shadow-sm flex items-start gap-3 text-left">
            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={20} />
            <div>
                <p className="text-slate-600 font-medium text-sm">
                    O link <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-500 font-bold break-all">/{brokenSlug}</span> pode estar quebrado ou ter sido removido.
                </p>
            </div>
        </div>

        <button 
            onClick={onNavigateHome}
            className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-primary transition-all shadow-lg hover:shadow-primary/30 flex items-center justify-center gap-2 mx-auto"
        >
            <Home size={16} /> Voltar para o Início
        </button>
      </div>

      {/* Suggestion Section */}
      <div className="max-w-6xl w-full">
        <div className="flex items-center gap-4 mb-8">
            <div className="h-[1px] flex-1 bg-gray-200"></div>
            <span className="text-xs font-black text-gray-400 uppercase tracking-[0.2em]">
                {suggestions.length > 0 && brokenSlug 
                    ? `Conteúdo Relacionado a "${brokenSlug.replace(/-/g, ' ')}"` 
                    : "Talvez você goste disto"}
            </span>
            <div className="h-[1px] flex-1 bg-gray-200"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {suggestions.map((item, idx) => (
                <div 
                    key={idx} 
                    onClick={() => {
                        if (item.type === 'news') onNavigateToArticle(item.data);
                        else window.open(item.data.link, '_blank');
                    }}
                    className="bg-white rounded-3xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all cursor-pointer group flex flex-col h-full"
                >
                    <div className="h-40 overflow-hidden relative bg-gray-100">
                        <img src={item.data.imageUrl} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" alt="" />
                        <div className="absolute top-3 left-3">
                            {item.type === 'news' ? (
                                <span className="px-3 py-1 bg-white/90 backdrop-blur-md rounded-lg text-[10px] font-black uppercase tracking-widest text-primary flex items-center gap-1 shadow-sm">
                                    <Newspaper size={10} /> Blog
                                </span>
                            ) : (
                                <span className="px-3 py-1 bg-slate-900/90 backdrop-blur-md rounded-lg text-[10px] font-black uppercase tracking-widest text-white flex items-center gap-1 shadow-sm">
                                    <Package size={10} /> Produto
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                        <h3 className="font-bold text-slate-900 mb-2 line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                            {item.data.title}
                        </h3>
                        <p className="text-xs text-slate-500 font-medium line-clamp-2 mb-4 flex-1">
                            {item.type === 'news' ? item.data.excerpt : item.data.description}
                        </p>
                        <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest group-hover:text-primary transition-colors">
                            Acessar <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                        </div>
                    </div>
                </div>
            ))}
        </div>
      </div>

    </div>
  );
};

export default NotFoundPage;
